import axios, { AxiosError } from 'axios'

/**
 * The one configured client. All calls go to the corporate website itself, which proxies to capi:
 * same origin, so no CORS, no base URL baked into the bundle, and the access token stays on the
 * server.
 *
 * The timeout sits above the 15 seconds the proxy allows itself, so an upstream timeout comes back
 * as a readable error instead of as a cancelled request.
 *
 * X-Requested-With marks the call as an XHR, the same as the keuze engines do. It does not avoid
 * the trailing slash redirect: the server rewrites every extensionless path before PHP sees it, so
 * the service urls carry the slash themselves.
 */
const TIMEOUT_MS = 20_000

export const client = axios.create({
  timeout: TIMEOUT_MS,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  },
})

const abortControllers = new Map<string, AbortController>()

/**
 * Aborts an earlier call with the same key. A visitor who adjusts a number and submits again gets
 * the answer to the last question, not to the one before it.
 */
export function cancelPendingRequest(key: string): AbortSignal {
  abortControllers.get(key)?.abort()

  const controller = new AbortController()
  abortControllers.set(key, controller)

  return controller.signal
}

export const isCancelled = axios.isCancel

/**
 * Rate limited by capi: too many calls from this visitor in a minute. Worth its own state, because
 * retrying right away keeps failing until the minute is over.
 */
export function isThrottled(error: unknown): boolean {
  return error instanceof AxiosError && error.response?.status === 429
}

/**
 * The sentence capi sends with a 429 (see ApiServiceProvider), which owns this wording just like
 * the validation messages. Empty when the throttling came from somewhere without a body of its
 * own, a proxy or the web server; the caller then falls back to its own text.
 */
export function throttleMessage(error: unknown): string {
  if (!isThrottled(error)) {
    return ''
  }

  const body = (error as AxiosError).response?.data as LaravelErrorBody | undefined

  return typeof body?.message === 'string' ? body.message : ''
}

type LaravelErrorBody = {
  message?: string
  errors?: Record<string, string[]>
}

/**
 * The field messages from a Laravel 422 response, keyed by the field capi named and kept to one
 * message per field: the first is the one the visitor has to act on. Empty when the error is
 * something else, so the caller can fall back to a general message.
 *
 * capi owns the wording, here and in the action plan form; the tool only decides where a message
 * lands.
 */
export function validationErrors(error: unknown): Record<string, string> {
  if (!(error instanceof AxiosError)) {
    return {}
  }

  const body = error.response?.data as LaravelErrorBody | undefined

  if (!body?.errors) {
    return {}
  }

  return Object.fromEntries(
    Object.entries(body.errors)
      .filter(([, messages]) => messages.length > 0)
      .map(([field, messages]) => [field, messages[0]]),
  )
}
